import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { BarChart3, Bot, ChevronRight, PhoneCall, Workflow } from 'lucide-react'
import {
  getApiClient,
  type GiftSummaryDataStatus,
  type GiftSummaryOverviewResponse,
} from '@/lib/api'
import { useResolvedWorkspace } from '@/hooks/useWorkspaces'
import {
  AsOfLabel,
  ErrorRow,
  fmtInt,
  fmtPct,
  LoadingRow,
  PageHeader,
  Section,
} from './shared'

interface TopJudgment {
  id: string
  summary: string
}

/** 未接続 / データなしの集計行 */
function connectionRows(overview: GiftSummaryOverviewResponse | null) {
  const rows: Array<{ label: string; status: GiftSummaryDataStatus }> = []
  if (overview) {
    rows.push({ label: 'コール', status: overview.calls.dataStatus })
    rows.push({ label: 'AI-SDR', status: overview.aiSdr.dataStatus })
    rows.push({ label: 'AX', status: overview.ax.dataStatus })
  }
  return rows
}

export function SummaryOverviewPage() {
  const [overview, setOverview] = useState<GiftSummaryOverviewResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [judgments, setJudgments] = useState<TopJudgment[]>([])
  const { workspaceId } = useResolvedWorkspace()

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    void getApiClient()
      .getGiftSummaryOverview()
      .then((result) => {
        if (!cancelled) {
          setOverview(result)
          setError(false)
          setLoading(false)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true)
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!workspaceId) return
    let cancelled = false
    void getApiClient()
      .getMeetingCenterLatest(workspaceId)
      .then((snapshot) => {
        if (cancelled || !snapshot) return
        setJudgments(
          snapshot.snapshot.decisions
            .filter((d) => d.state === 'proposed')
            .slice(0, 3)
            .map((d) => ({ id: d.decisionId, summary: d.summary })),
        )
      })
      .catch(() => {
        // 判断の取得失敗は概要を隠さない
      })
    return () => {
      cancelled = true
    }
  }, [workspaceId])

  const closedWon = overview?.ax.stages.find((s) => s.key === 'closed_won')?.count ?? null
  const rows = connectionRows(overview)
  const anyGap = rows.some((r) => r.status !== 'ok')

  return (
    <div className="space-y-6">
      <PageHeader title="サマリー" sub="コール / AI-SDR / AX の要点と今週の判断" />

      {loading ? (
        <LoadingRow label="サマリー" />
      ) : error ? (
        <ErrorRow label="サマリー" />
      ) : overview && overview.dataStatus === 'not_connected' ? (
        <Section title="未接続">
          <p className="text-sm text-muted-foreground">
            サマリーの計測データに接続できていません。AX_SOURCE_SCOPE が設定され、
            日次集計（cb_daily_metric_counts）が蓄積されると表示されます。
          </p>
        </Section>
      ) : (
        <>
          {/* 3 事業カード */}
          <div className="grid gap-4 md:grid-cols-3">
            <SummaryCard
              to="/summary/calls"
              icon={<PhoneCall className="h-5 w-5" aria-hidden />}
              title="コール"
              latestDay={overview?.calls.latestDay ?? null}
              primary={
                overview?.calls.monthCalls !== null
                  ? `今月コール ${fmtInt(overview?.calls.monthCalls)}件`
                  : '未計測'
              }
              secondary={
                overview?.calls.monthApoRate !== null
                  ? `アポ率 ${fmtPct(overview?.calls.monthApoRate)}`
                  : null
              }
              computedAt={overview?.computedAt ?? null}
            />
            <SummaryCard
              to="/summary/ai-sdr"
              icon={<Bot className="h-5 w-5" aria-hidden />}
              title="AI-SDR"
              latestDay={null}
              primary={overview?.aiSdr.dataStatus === 'not_connected' ? '未接続' : '未計測'}
              secondary={null}
              computedAt={overview?.computedAt ?? null}
            />
            <SummaryCard
              to="/summary/ax"
              icon={<Workflow className="h-5 w-5" aria-hidden />}
              title="AX"
              latestDay={null}
              primary={
                overview?.ax.pipelineTotalQuoteAmountYen !== null
                  ? `受注見込み ${fmtInt(overview?.ax.pipelineTotalQuoteAmountYen)}円`
                  : '未計測'
              }
              secondary={
                overview?.ax.pipelineTotalDeals !== null
                  ? `商談 ${fmtInt(overview?.ax.pipelineTotalDeals)}件`
                  : null
              }
              computedAt={overview?.computedAt ?? null}
            />
          </div>

          {/* 12月末判定 */}
          <Section title="12月末判定">
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
              <div>
                <p className="text-sm text-muted-foreground">KGI（決定事項 D-010）</p>
                <p className="text-lg font-semibold">12月単月の成約 合格5社 / 目標7社</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">実績（AX 成約）</p>
                <p className="text-lg font-semibold tabular-nums">
                  {closedWon !== null ? `${fmtInt(closedWon)}社` : '未計測'}
                </p>
              </div>
            </div>
          </Section>

          {/* 今週の判断 */}
          <Section title="今週の判断" note="週次会議の Top 判断（最大3件）">
            {judgments.length > 0 ? (
              <ol className="grid gap-2">
                {judgments.map((j, i) => (
                  <li
                    key={j.id}
                    className="grid grid-cols-[1.75rem_1fr] gap-2 rounded-md border bg-background px-3 py-2 text-sm"
                  >
                    <span className="font-semibold tabular-nums text-primary">{i + 1}</span>
                    <span>{j.summary}</span>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-muted-foreground">今週の判断事項は未登録です</p>
            )}
          </Section>

          {/* 欠測・未接続 */}
          {anyGap && (
            <Section title="欠測・未接続">
              <ul className="grid gap-1 text-sm">
                {rows
                  .filter((r) => r.status !== 'ok')
                  .map((r) => (
                    <li key={r.label} className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-amber-400" aria-hidden />
                      <span className="font-medium">{r.label}</span>
                      <span className="text-muted-foreground">
                        {r.status === 'not_connected' ? '未接続' : 'データなし'}
                      </span>
                    </li>
                  ))}
              </ul>
            </Section>
          )}
        </>
      )}

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <BarChart3 className="h-4 w-4" aria-hidden />
        集計 = CH の日次集計（Salesforce 直集計・集計値のみ保持）。率は読み出し時に計算
      </div>
    </div>
  )
}

function SummaryCard({
  to,
  icon,
  title,
  latestDay,
  primary,
  secondary,
  computedAt,
}: {
  to: string
  icon: React.ReactNode
  title: string
  latestDay: string | null
  primary: string
  secondary: string | null
  computedAt: string | null
}) {
  return (
    <Link
      to={to}
      className="group rounded-lg border bg-card p-4 shadow-sm transition-colors hover:border-primary"
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="flex items-center gap-2 font-semibold">
          {icon}
          {title}
        </span>
        <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
      </div>
      <p className="text-sm text-muted-foreground">
        最新日 {latestDay ?? '未計測'}
      </p>
      <p className="mt-1 font-semibold">{primary}</p>
      {secondary && <p className="text-sm text-muted-foreground">{secondary}</p>}
      <div className="mt-2">
        <AsOfLabel computedAt={computedAt} />
      </div>
    </Link>
  )
}
